/* istanbul ignore file */
import { FormSection, FormRow, MediumFormRow, BigFormRow } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import AccountTypeDropdown from 'domain/sales/account/shared/input/AccountTypeDropdown';
import { AddressInput, Checkbox, PhoneInput, TextArea, TextInput, useFormContext } from 'framework/form';
import { Box } from '@mui/material';
import { useAuthorization } from 'framework/authorization';
import IndustryDropdown from 'domain/sales/shared/input/IndustryDropdown';
import OwnerInput from 'domain/sales/shared/input/OwnerInput';
import { AccountNameInput as AccountInput } from 'domain/shared/account';
import { CrmAttachmentUploaderInput } from 'domain/shared/artifact/input';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { GenerateCodeNameButton } from 'domain/shared/core/codename';
import useAccountManagementService from '../useAccountManagementService';
import {
  CHANGE_ACCOUNT_OWNER,
  DELIVERY_ACCOUNT_PAUSE_TICKET_RETENTION,
  DELIVERY_CONTEXT,
  READ_FULL_ACCOUNTS_LIST,
} from '../model';

const AccountForm = () => {
  const { isGranted } = useAuthorization();
  const { isFeatureEnabled } = useFeatureToggle();
  const { data = {}, setFormData = () => {} } = useFormContext();
  const [isButtonDisplayed] = useState(!data.accountName);
  const { getLitigationHold } = useAccountManagementService();

  const setNewAccountName = (newName: any) => {
    setFormData({
      ...data,
      accountName: newName,
    });
  };

  useEffect(() => {
    if (
      !data.accountId ||
      !isFeatureEnabled(Features.ACCOUNT_TICKET_RETENTION_ON_HOLD) ||
      !isGranted(DELIVERY_ACCOUNT_PAUSE_TICKET_RETENTION, DELIVERY_CONTEXT)
    ) {
      return;
    }

    let cancelled = false;

    getLitigationHold(data.accountId)
      .then(retention => {
        if (cancelled || !retention) {
          return;
        }

        const isOnLitigationHold = retention.isOnLitigationHold ?? false;
        const litigationHoldReason = retention.litigationHoldReason ?? '';

        setFormData({
          ...data,
          isOnLitigationHold,
          originalIsOnLitigationHold: isOnLitigationHold,
          litigationHoldReason,
          originalLitigationHoldReason: litigationHoldReason,
        });
      })
      .catch(() => {
        // ignore, keep form usable without delivery data
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when account id changes; other deps are stable
  }, [data.accountId]);

  return (
    <>
      <FormSection title="Account information">
        <FormRow>
          <TextInput label="Original name" source="originalName" />
          <TextInput label="Account name" source="accountName" disabled />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <GenerateCodeNameButton
              source="accountName"
              isVisible={isButtonDisplayed}
              setCodeNameMethod={setNewAccountName}
            />
          </Box>
        </FormRow>
        <FormRow>
          <AccountTypeDropdown label="Account type" source="accountType" />
          <TextInput label="Website" source="website" />
          <PhoneInput label="Phone" source="phoneNumber" hasExtension />
          <IndustryDropdown label="Industry" source="industry" />
          <TextInput label="No. of workers" source="employeesNumber" />
          <AccountInput label="Parent account" source="parentAccountId" accountNameSource="parentAccountName" />
        </FormRow>
        <FormRow>
          <OwnerInput
            label="Account owner"
            source="ownerId"
            dropdownPlaceholder="Choose account owner"
            isUserCanChange={isGranted(CHANGE_ACCOUNT_OWNER) || isGranted(READ_FULL_ACCOUNTS_LIST)}
          />
        </FormRow>
        {isFeatureEnabled(Features.ACCOUNT_TICKET_RETENTION_ON_HOLD) &&
          isGranted(DELIVERY_ACCOUNT_PAUSE_TICKET_RETENTION, DELIVERY_CONTEXT) && (
            <>
              <FormRow>
                <Checkbox
                  label="Is on litigation hold?"
                  source="isOnLitigationHold"
                  tooltip="When this flag is set to true, information related to this account will be preserved for litigation purposes and will not be deleted according to standard retention policies until the flag is set to false."
                />
              </FormRow>
              <FormRow>
                <TextArea
                  label="Litigation hold reason"
                  source="litigationHoldReason"
                  isVisible={(rowData: any) => Boolean(rowData.isOnLitigationHold)}
                />
              </FormRow>
            </>
          )}
      </FormSection>

      <FormSection title="Address information">
        <AddressInput source="address" addressLine1Label="Street" zipLabel="Zip/Postal code" />
      </FormSection>

      <FormSection title="Description information">
        <MediumFormRow>
          <TextArea source="accountDescription" label="Account description" />
        </MediumFormRow>
        <BigFormRow>
          <CrmAttachmentUploaderInput label="Attachments" source="attachmentsIds" />
        </BigFormRow>
      </FormSection>
    </>
  );
};

export default AccountForm;
